import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { ChevronsUpDown, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export function SearchableSelect({
  value,
  onValueChange,
  options = [],        // [{ value, label }]
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  className,
  allowCustom = false,
  onAddCustom,
  'data-testid': testId,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  const handleAddCustom = () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    if (onAddCustom) onAddCustom(trimmed);
    onValueChange(trimmed);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between bg-background border-border font-normal h-9 text-sm",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
          aria-label={ariaLabel}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-card border-border"
        style={{ width: triggerWidth || 'var(--radix-popover-trigger-width)' }}
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            className="text-xs"
          />
          <CommandList
            className="max-h-52"
            onWheel={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
              {emptyMessage}
              {allowCustom && search.trim() && (
                <button
                  onClick={handleAddCustom}
                  className="flex items-center gap-1 mx-auto mt-2 text-primary hover:underline cursor-pointer text-xs"
                  data-testid="add-custom-option"
                >
                  <Plus className="h-3 w-3" /> Add &quot;{search.trim()}&quot;
                </button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {options
                .filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
                .map(o => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => {
                      onValueChange(o.value === value ? '' : o.value);
                      setSearch('');
                      setOpen(false);
                    }}
                    className="text-xs cursor-pointer"
                  >
                    <Check className={cn("mr-1.5 h-3 w-3", value === o.value ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              {allowCustom && search.trim() && !options.some(o => o.label.toLowerCase() === search.trim().toLowerCase()) && (
                <CommandItem
                  onSelect={handleAddCustom}
                  className="text-xs cursor-pointer text-primary"
                >
                  <Plus className="mr-1.5 h-3 w-3" />
                  Add &quot;{search.trim()}&quot;
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
